import humps from 'humps'
import isBlank from 'is-blank'
import get from 'just-safe-get'
import { foldazApi } from 'api'
import { NOTIFICATION_TYPE_ERROR } from '../modules/notifications/types'

export const CREATE_REQUEST = 'CREATE_REQUEST'
export const CREATE_SUCCESS = 'CREATE_SUCCESS'
export const CREATE_ERROR = 'CREATE_ERROR'
export const SHOW_REQUEST = 'SHOW_REQUEST'
export const SHOW_SUCCESS = 'SHOW_SUCCESS'
export const SHOW_ERROR = 'SHOW_ERROR'
export const UPDATE_REQUEST = 'UPDATE_REQUEST'
export const UPDATE_SUCCESS = 'UPDATE_SUCCESS'
export const UPDATE_ERROR = 'UPDATE_ERROR'
export const DESTROY_REQUEST = 'DESTROY_REQUEST'
export const DESTROY_SUCCESS = 'DESTROY_SUCCESS'
export const DESTROY_ERROR = 'DESTROY_ERROR'
export const SET_ID = 'SET_ID'

export function buildPath (path, params = {}) {
  const keys = Object.keys(params)
  keys.forEach(key => {
    path = path.replace(`:${key}`, params[key])
  })
  return path
}

export function createAjaxAction (name, method, path, resourceName) {
  return ({ commit, dispatch, state }, params = {}) => {
    let requestData
    let responseObj
    let config = {}
    const { form } = state
    const formData = new FormData()

    Object.keys(form).forEach(key => {
      const field = humps.decamelize(isBlank(resourceName) ? key : `${resourceName}[${key}]`)

      if (form[key] && form[key].constructor === Array) {
        const value = form[key].map(v => {
          return v.value || v
        }).join(',')

        formData.append(field, value)
      } else if(form[key] && form[key].constructor === File) {
        config['headers'] = { 'Content-Type': 'multipart/form-data' }
        formData.append(field, form[key], form[key].name)
      } else {
        formData.append(field, form[key])
      }
    })

    requestData = formData

    // move requestData for delete requests into config.data
    // https://github.com/axios/axios/issues/897#issuecomment-343715381
    if (method === 'delete') {
      let data = {}
      data[resourceName] = {}

      Object.keys(form).forEach(key => {
        data[resourceName][humps.decamelize(key)] = form[key]
      })

      requestData = {
        data: { ...data }
      }
    }

    commit(`${name.toUpperCase()}_REQUEST`)
    return foldazApi[method](buildPath(path, params), requestData, config)
      .then(response => {
        responseObj = response
        if (response.constructor === Error) {
          throw response
        } else {
          const attributes = get(response, `data.${resourceName}`)
          if (attributes) {
            Object.keys(attributes).forEach(attribute => {
              if (form.hasOwnProperty(attribute)) {
                let dispatchName = attribute.charAt(0).toUpperCase() + attribute.slice(1)
                dispatchName = `set${dispatchName}`
                const value = attributes[attribute]
                dispatch(dispatchName, value)
              }
            })
          }
          commit(`${name.toUpperCase()}_SUCCESS`)
        }
      })
      .then(() => responseObj)
      .catch(error => {
        console.error(error)
        commit(`${name.toUpperCase()}_ERROR`, error)
        const message = get(error, 'response.data.message')
        if (message) {
          dispatch('addNotification', { message, type: NOTIFICATION_TYPE_ERROR }, { root: true })
        }
        throw error
      })
  }
}

export function createResetErrorAction (name) {
  if (isBlank(name)) {
    throw new Error('name cannot be blank.')
  }
  return ({ commit }) => {
    commit(`${name.toUpperCase()}_ERROR`, null)
  }
}

export default ({
  resourcesName,
  resourcesPath,
  resourceName,
  resourcePath,
  params = {}
}) => {
  const state = {
    form: {
      id: null
    },
    createRequest: {
      isLoading: false,
      error: null
    },
    showRequest: {
      isLoading: false,
      error: null
    },
    updateRequest: {
      isLoading: false,
      error: null
    },
    destroyRequest: {
      isLoading: false,
      error: null
    }
  }

  const mutations = {
    [CREATE_REQUEST] (state) {
      state.createRequest.isLoading = true
    },
    [CREATE_SUCCESS] (state) {
      state.createRequest.isLoading = false
      state.createRequest.error = null
    },
    [CREATE_ERROR] (state, error) {
      state.createRequest.isLoading = false
      state.createRequest.error = error
    },
    [SHOW_REQUEST] (state) {
      state.showRequest.isLoading = true
    },
    [SHOW_SUCCESS] (state) {
      state.showRequest.isLoading = false
      state.showRequest.error = null
    },
    [SHOW_ERROR] (state, error) {
      state.showRequest.isLoading = false
      state.showRequest.error = error
    },
    [UPDATE_REQUEST] (state) {
      state.updateRequest.isLoading = true
    },
    [UPDATE_SUCCESS] (state) {
      state.updateRequest.isLoading = false
      state.updateRequest.error = null
    },
    [UPDATE_ERROR] (state, error) {
      state.updateRequest.isLoading = false
      state.updateRequest.error = error
    },
    [DESTROY_REQUEST] (state) {
      state.destroyRequest.isLoading = true
    },
    [DESTROY_SUCCESS] (state) {
      state.destroyRequest.isLoading = false
      state.destroyRequest.error = null
    },
    [DESTROY_ERROR] (state, error) {
      state.destroyRequest.isLoading = false
      state.destroyRequest.error = error
    },
    [SET_ID] (state, value) {
      state.form.id = value
    }
  }

  const actions = {}
  if (resourcesPath) {
    actions.create = createAjaxAction('CREATE', 'post', resourcesPath, resourcesName)
    actions.resetCreateErrors = createResetErrorAction('CREATE')
  }
  if (resourcePath) {
    actions.show = createAjaxAction('SHOW', 'get', resourcePath, resourceName)
    actions.resetShowErrors = createResetErrorAction('SHOW')

    actions.update = createAjaxAction('UPDATE', 'put', resourcePath, resourceName)
    actions.resetUpdateErrors = createResetErrorAction('UPDATE')

    actions.destroy = createAjaxAction('DESTROY', 'delete', resourcePath, resourceName)
    actions.resetDestroyErrors = createResetErrorAction('DESTROY')

    actions.setId = ({ commit }, value) => commit(SET_ID, value)
  }

  if (resourcesPath && resourcePath) {
    actions.submit = ({ dispatch, state }) => {
      if (state.form.id === null) {
        dispatch('create')
      } else {
        dispatch('update')
      }
    }
  }

  const getters = {
    formErrors: state => requestType => {
      const requestObj = state[`${requestType}Request`]
      const error = requestObj.error && get(requestObj.error, 'response.data') || {}
      return humps.camelizeKeys(error)
    },
    fieldError: (state, { formErrors }) => (requestType, field) => {
      const errors = formErrors(requestType)[field]
      if (errors) {
        return errors.join(', ')
      }
    }
  }

  return {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
  }
}



// WEBPACK FOOTER //
// ./src/store/utils/createFormModule.js